/**
 * `pipeline-status` — where every contract sits in the extraction pipeline ·
 * CLM-70 (D-70-1…D-70-5, adopted 11 Sep 2026 per recommendation; Gerry may
 * overturn; R-70-1 and D-71-0 bind the issue lists).
 *
 * One read-only `GET /v1/admin/pipeline/status` behind the Admin **Pipeline**
 * surface and the stage tracker on the Contract record. Nothing here writes:
 * `writes_audit_row: false` (D-70-2), no DDL, no new action — the gate is
 * `contract:read` (D-70-1) so `reader`/`contributor` see their own scope and
 * `admin` everything.
 *
 * ## `processing_status` is read as the pipeline tracker (D-70-5)
 *
 * 0005's own comment calls the column "Axis 2 — the pipeline state"; TD-04's
 * other axis is `lifecycle_status`. The eight values map onto five steps:
 * Pending → Converted → Pseudonymised → Extracted → Verified, with
 * `PendingVerification` sitting between the last two, and `ExtractionFailed` /
 * `manual_entry_pending` as the blocked and withheld shapes.
 *
 * ## Every figure and every reason is the database's, verbatim (CR-01, FR-27)
 *
 * Counts are one `count(*)` per status. A held, refused or failed row carries
 * the latest `AIRefusal` / `AIExtractionFailure` audit row's text as
 * `reason_text` — never a paraphrase — and the three lists are mutually
 * exclusive because each contract is classified by that one latest AI row
 * (`AIExtraction` included: a later success supersedes the failure, so a
 * recovered contract is in no list).
 * Pseudonymised / Verified times come from the 0046 trigger rows (there is no
 * column); `Verified` is never written today, so its step is `null` until the
 * FR-07 closure lands.
 *
 * ## `pending` is the queue, not an issue list
 *
 * The three issue lists carry only contracts with a problem, so the Pending tile
 * used to count contracts no list could show. `pending` lists every
 * `PendingExtraction` contract in scope — healthy, waiting for the next run —
 * in the runner's own pick order. Nothing about processing changes.
 *
 * ## Scope
 *
 * Every statement carries `toScopeSqlPredicate` on `c.root_contract_key`
 * (rules 9, 10). An out-of-scope or unknown `contract_key` is five steps with
 * `reached_utc: null`, not 403 — the `clause-matches` convention.
 */
import { z } from 'zod';
import { ContractKeySchema, IsoTimestampUtcSchema, PartyKeySchema } from '../primitives.js';
import { ContractTypeConfirmationSchema, ContractTypeSchema, PROCESSING_STATUSES, ProcessingStatusSchema, } from '../vocabulary.js';
import { TagFilterSchema } from './contract-tags.js';
const Count = z.number().int().min(0);
/**
 * The literal the pipeline runner writes as `failure_reason` when the §4A.5
 * leak detector holds a send (D-70-4). The API classifies a held contract by
 * `starts_with(failure_reason, HOLD_PREFIX)` and the stage tracker recognises
 * the hold in `ai_processing.failure_reason` with the same constant; the API's
 * queries test asserts string equality with `orchestrator/pipeline-runner.ts`.
 */
export const HOLD_PREFIX = 'the §4A.5 leak detector held the send:';
/** The five steps of the tracker, in pipeline order. */
export const PIPELINE_STAGES = ['PendingExtraction', 'Converted', 'Pseudonymised', 'Extracted', 'Verified'];
export const PipelineStageSchema = z.enum(PIPELINE_STAGES);
/**
 * Why a contract is held short of extraction.
 *
 * - `no_confirmed_alias` — the §4A.5 leak detector held the send; alias
 *   proposals wait for a human on Party aliases (D-70-4: classified by the
 *   runner's literal prefix, string-equality tested).
 * - `processing_restricted` — §4C, facts are entered manually.
 * - `sow_number_unconfirmed` — D-71-0: a SOW without a confirmed number, or a
 *   contract without a resolved primary party, is never picked by the runner.
 * - `extraction_failed` — reserved for the `ELSE` branch; a plain failure is
 *   listed under `failed`, not `held`.
 * - `unknown` — `ExtractionFailed` with no AI audit row to explain it.
 */
export const HELD_REASONS = [
    'no_confirmed_alias',
    'processing_restricted',
    'sow_number_unconfirmed',
    'extraction_failed',
    'unknown',
];
export const HeldReasonSchema = z.enum(HELD_REASONS);
/** The stage a listed issue sits at. */
export const PIPELINE_ISSUE_STAGES = ['intake', 'convert', 'pseudonymise', 'extract', 'manual_entry'];
export const PipelineIssueStageSchema = z.enum(PIPELINE_ISSUE_STAGES);
export const PipelineStatusRequestSchema = z
    .object({
    /**
     * Narrows all five statements. CLM-118 (B19): a filter for `OTHER` lists real
     * `OTHER` contracts only — a Type pending row (`OTHER` + `pending`) is never
     * returned for a type filter.
     */
    contract_type: ContractTypeSchema.optional(),
    /** When present the response carries `timeline` for this one contract; out of scope ⇒ five null steps, not 403. */
    contract_key: ContractKeySchema.optional(),
    /** CLM-77 — all-of tag filter; narrows all five statements. Repeated `tags=` query parameters. */
    tags: TagFilterSchema.optional(),
})
    .strict();
/**
 * One key per `PROCESSING_STATUSES` value, always present (zero when absent).
 * The eight keys are spelled out so the compiler checks them; the routes test
 * asserts `Object.keys(counts)` equals `PROCESSING_STATUSES` (correction 6).
 */
export const StatusCountsSchema = z
    .object({
    PendingExtraction: Count,
    manual_entry_pending: Count,
    Converted: Count,
    Pseudonymised: Count,
    Extracted: Count,
    PendingVerification: Count,
    Verified: Count,
    ExtractionFailed: Count,
})
    .strict();
/* The eight spelled-out keys must be the vocabulary's eight, no more, no fewer — both directions at compile time:
 * every vocabulary value is a key of StatusCounts ... */
const _countsKeysAreTheVocabulary = PROCESSING_STATUSES;
void _countsKeysAreTheVocabulary;
/* ... and every key of StatusCounts is a vocabulary value (an extra spelled-out key makes this `never`). */
const _countsKeysAreOnlyTheVocabulary = true;
void _countsKeysAreOnlyTheVocabulary;
const PipelineItemBase = z.object({
    contract_key: ContractKeySchema,
    root_contract_key: ContractKeySchema,
    /** Primary party key (FR-14); `null` when the contract has no primary party row (correction 5). */
    party_key: PartyKeySchema.nullable(),
    primary_party_display_name: z.string().min(1).max(200).nullable(),
    sow_number: z.string().max(32).nullable(),
    processing_status: ProcessingStatusSchema,
    stage: PipelineIssueStageSchema,
    recorded_utc: IsoTimestampUtcSchema,
    /** The audit row's text, verbatim (CR-01); `null` when no row explains it. */
    reason_text: z.string().max(4000).nullable(),
    /**
     * `rerun-pipeline-contract` may reset this row: `ExtractionFailed` and the latest AI audit row is absent (the failure
     * happened before the boundary, CLM-79) or an `AIExtractionFailure` (a provider or request failure, CLM-80 D-80-2).
     * Never true when the latest row is an `AIRefusal` — rule 6.
     */
    is_rerunnable: z.boolean(),
});
export const HeldPipelineItemSchema = PipelineItemBase.extend({ held_reason: HeldReasonSchema });
export const RefusedPipelineItemSchema = PipelineItemBase.extend({
    refusal_category: z.string().max(120).nullable(),
});
export const FailedPipelineItemSchema = PipelineItemBase.extend({
    attempt_count: Count,
    /** `processing_status = 'Pseudonymised'` — the only trace of the retryable branch; the next run resumes there. */
    is_retryable: z.boolean(),
});
/**
 * One contract waiting for its first pipeline run — `processing_status =
 * 'PendingExtraction'`. Not an issue row: nothing is wrong with it, it is queued.
 * Listed so the **Pending** tile's figure has rows behind it; the three issue
 * lists never carried a healthy queued contract. A `PendingExtraction` contract
 * the runner will not pick (D-71-0) is *also* in `held` as
 * `sow_number_unconfirmed` — the two lists answer different questions.
 */
export const PendingPipelineItemSchema = z.object({
    contract_key: ContractKeySchema,
    root_contract_key: ContractKeySchema,
    contract_type: ContractTypeSchema,
    /** CLM-110 (R-110-23) — rendered beside `contract_type` as its `provisional` tag. */
    contract_type_confirmation: ContractTypeConfirmationSchema,
    /** Primary party key (FR-14); `null` when the contract has no primary party row. */
    party_key: PartyKeySchema.nullable(),
    primary_party_display_name: z.string().min(1).max(200).nullable(),
    sow_number: z.string().max(32).nullable(),
    processing_status: z.literal('PendingExtraction'),
    /** `contract.created_utc` — when the row entered the queue. */
    created_utc: IsoTimestampUtcSchema,
});
export const PipelineTimelineStepSchema = z.object({
    stage: PipelineStageSchema,
    reached_utc: IsoTimestampUtcSchema.nullable(),
    /** 'column' = converted_utc / extracted_utc / created_utc; 'audit' = a 0046 Create/Update/StatusChange row. */
    source: z.enum(['column', 'audit']).nullable(),
});
export const PipelineStatusResponseSchema = z
    .object({
    counts: StatusCountsSchema,
    /** A real scoped count over `contract` — the sum of `counts`. */
    total_in_scope: Count,
    held: z.array(HeldPipelineItemSchema),
    refused: z.array(RefusedPipelineItemSchema),
    failed: z.array(FailedPipelineItemSchema),
    /** Every `PendingExtraction` contract in scope, in the order the runner picks them (`contract_key`). */
    pending: z.array(PendingPipelineItemSchema),
    /** Present only when the request named a `contract_key`. */
    timeline: z.array(PipelineTimelineStepSchema).length(5).nullable(),
    generated_utc: IsoTimestampUtcSchema,
})
    .strict();
//# sourceMappingURL=pipeline-status.js.map